import {
  type Context,
  type TextMapGetter,
  type TextMapPropagator,
  type TextMapSetter,
  TraceFlags,
  trace,
} from '@opentelemetry/api'

const DATADOG_TRACE_ID = 'x-datadog-trace-id'
const DATADOG_PARENT_ID = 'x-datadog-parent-id'
const DATADOG_SAMPLING_PRIORITY = 'x-datadog-sampling-priority'
const DATADOG_ORIGIN = 'x-datadog-origin'

const DATADOG_HEADER_FIELDS = [
  DATADOG_TRACE_ID,
  DATADOG_PARENT_ID,
  DATADOG_SAMPLING_PRIORITY,
  DATADOG_ORIGIN,
] as const

/**
 * Injects/extracts Datadog trace headers for RUM <-> server <-> API correlation.
 * Aligns with @datadog/browser-rum `propagatorTypes: ['datadog']` (64-bit trace id).
 */
export class DatadogPropagator implements TextMapPropagator {
  constructor(private readonly origin = 'main') {}

  fields(): string[] {
    return [...DATADOG_HEADER_FIELDS]
  }

  inject(context: Context, carrier: unknown, setter: TextMapSetter): void {
    const span = trace.getSpan(context)
    if (!span) {
      return
    }

    const spanContext = span.spanContext()
    if (!trace.isSpanContextValid(spanContext)) {
      return
    }

    setter.set(
      carrier,
      DATADOG_TRACE_ID,
      datadogTraceIdFromOtelTraceId(spanContext.traceId),
    )
    setter.set(
      carrier,
      DATADOG_PARENT_ID,
      datadogParentIdFromOtelSpanId(spanContext.spanId),
    )
    setter.set(
      carrier,
      DATADOG_SAMPLING_PRIORITY,
      spanContext.traceFlags & TraceFlags.SAMPLED ? '1' : '0',
    )
    setter.set(carrier, DATADOG_ORIGIN, this.origin)
  }

  extract(context: Context, carrier: unknown, getter: TextMapGetter): Context {
    const datadogTraceId = getHeader(getter, carrier, DATADOG_TRACE_ID)
    const datadogParentId = getHeader(getter, carrier, DATADOG_PARENT_ID)

    if (!datadogTraceId || !datadogParentId) {
      return context
    }

    const spanContext = {
      traceId: otelTraceIdFromDatadogTraceId(datadogTraceId),
      spanId: otelSpanIdFromDatadogParentId(datadogParentId),
      isRemote: true,
      traceFlags:
        getHeader(getter, carrier, DATADOG_SAMPLING_PRIORITY) === '1'
          ? TraceFlags.SAMPLED
          : TraceFlags.NONE,
    }

    if (!trace.isSpanContextValid(spanContext)) {
      return context
    }

    return trace.setSpanContext(context, spanContext)
  }
}

function getHeader(
  getter: TextMapGetter,
  carrier: unknown,
  key: string,
): string | undefined {
  const value = getter.get(carrier, key)
  if (Array.isArray(value)) {
    return value[0]
  }
  return value
}

/** Lower 64 bits of OTel trace id as decimal (matches browser RUM datadog propagator). */
export function datadogTraceIdFromOtelTraceId(traceId: string): string {
  const lowerHex = traceId.length === 32 ? traceId.slice(-16) : traceId
  return BigInt(`0x${lowerHex}`).toString()
}

/** OTel 128-bit trace id with zero high bits (matches RUM tracecontext padding). */
export function otelTraceIdFromDatadogTraceId(datadogTraceId: string): string {
  const lowerHex = BigInt(datadogTraceId)
    .toString(16)
    .padStart(16, '0')
    .slice(-16)
  return `0000000000000000${lowerHex}`
}

export function datadogParentIdFromOtelSpanId(spanId: string): string {
  return BigInt(`0x${spanId}`).toString()
}

export function otelSpanIdFromDatadogParentId(datadogParentId: string): string {
  return BigInt(datadogParentId).toString(16).padStart(16, '0').slice(-16)
}
